/**
 * The last loaded Candidate Requests list, for THIS browser tab and session only.
 *
 * The tab unmounts whenever the recruiter looks at another board tab, so every return trip used to
 * re-run the whole pipeline — Gmail, Lever, the drafting — behind a spinner, for a list that had
 * not changed. Keeping the last result here renders the queue instantly and lets the refetch land
 * quietly underneath it.
 *
 * Module scope, deliberately NOT localStorage like boardCache: these items carry candidates' actual
 * email text. In memory it dies with the page; on disk it would outlive the session and the sign-in.
 * On top of that there is exactly ONE slot and every read checks the owner, so a sign-out and a
 * sign-in as someone else can neither read the previous user's mail nor keep it once the new
 * mailbox loads.
 */
import type { InboundRequest } from '../../types/taActionBoard';

interface CachedRequests {
  owner: string;
  items: InboundRequest[];
}

let cache: CachedRequests | null = null;

/**
 * Drop the slot outright — for sign-out, where "unreadable by anyone else" is not the same as
 * "gone". Signing out does not reload this SPA, so without it one recruiter's candidate names,
 * addresses and message text would sit in memory until the next mailbox happened to overwrite them.
 */
export function clearRequestsCache(): void {
  cache = null;
}

const normalize = (email: string) => `${email || ''}`.trim().toLowerCase();

/** The cached list for this mailbox, or null when there is none (or it belongs to someone else). */
export function readRequestsCache(ownerEmail: string): InboundRequest[] | null {
  const owner = normalize(ownerEmail);
  if (!owner || !cache || cache.owner !== owner) return null;
  return cache.items;
}

export function writeRequestsCache(ownerEmail: string, items: InboundRequest[]): void {
  const owner = normalize(ownerEmail);
  if (!owner) return;
  cache = { owner, items };
}

/**
 * Apply a local change (a thread marked read, a draft saved) to the cached list.
 *
 * Without this the cache would keep re-serving work the recruiter has already done: they mark three
 * threads as read, switch tabs, come back, and all three are waiting for them again until the
 * refetch lands.
 */
export function updateRequestsCache(
  ownerEmail: string,
  update: (items: InboundRequest[]) => InboundRequest[]
): void {
  const owner = normalize(ownerEmail);
  if (!owner || !cache || cache.owner !== owner) return;
  cache = { owner, items: update(cache.items) };
}
